import React, { useMemo, useRef, useState } from "react";
import { consoleApi } from "../../../services/consoleApiService";
import { useConsoleFetch } from "../../../hooks/useConsoleFetch";

// Merge MongoDB-stored uploads with the filesystem stock library so the user
// sees every available screenshot in one grid. Stock items are read-only
// (committed to the repo, not deletable via UI) and tagged with source: 'stock'.
function normalizeUploaded(item) {
  return {
    id: item.id,
    url: item.url,
    label: item.label,
    category: item.category || "general",
    city: item.city || null,
    width: item.width,
    height: item.height,
    source: "uploaded",
    deletable: true,
  };
}

function flattenStock(stockData) {
  if (!stockData || !Array.isArray(stockData.categories)) return [];
  const out = [];
  for (const cat of stockData.categories) {
    for (const item of cat.items || []) {
      out.push({
        id: `stock:${cat.name}/${item.filename}`,
        url: item.url,
        label: item.label,
        category: cat.name,
        source: "stock",
        deletable: false,
      });
    }
  }
  return out;
}

const CATEGORY_OPTIONS = [
  { value: "general", label: "General" },
  { value: "app-screens", label: "App Screens" },
  { value: "feature", label: "Feature" },
  { value: "club", label: "Club" },
  { value: "meeting", label: "Meeting" },
  { value: "ui-detail", label: "UI Detail" },
];

function Lightbox({ item, onClose }) {
  if (!item) return null;
  return (
    <div className="ch-lib__lightbox" onClick={onClose}>
      <div className="ch-lib__lightbox-inner" onClick={(e) => e.stopPropagation()}>
        <img src={item.url} alt={item.label} />
        <div className="ch-lib__lightbox-meta">
          <div className="ch-lib__lightbox-topic">{item.label}</div>
          <div className="ch-lib__lightbox-tags">
            {item.city && <span className="content-hub__tag content-hub__tag--posted">{item.city}</span>}
            <span className="content-hub__tag">{item.category}</span>
            {item.width && item.height && (
              <span className="content-hub__tag content-hub__tag--soft">
                {item.width}×{item.height}
              </span>
            )}
          </div>
          <div className="ch-lib__lightbox-actions">
            <a href={item.url} target="_blank" rel="noreferrer" className="content-hub__btn">
              Open full size
            </a>
            <button type="button" className="content-hub__btn" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ScreenshotsView() {
  const [filterCategory, setFilterCategory] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [active, setActive] = useState(null);
  const [uploadCategory, setUploadCategory] = useState("general");
  const [uploadCity, setUploadCity] = useState("");
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [botCity, setBotCity] = useState("Miami");
  const [botCount, setBotCount] = useState(3);
  const [botResult, setBotResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [editCity, setEditCity] = useState("");
  const fileInputRef = useRef(null);

  const { data: uploaded, loading: loadingUploaded, refetch } = useConsoleFetch(
    () => consoleApi.content.listScreenshots().then((r) => (Array.isArray(r) ? r : [])),
    [],
    { refetchOnWindowFocus: false }
  );

  const { data: stock, loading: loadingStock } = useConsoleFetch(
    () => consoleApi.content.stockLibrary(),
    [],
    { refetchOnWindowFocus: false }
  );

  const { data: cities } = useConsoleFetch(
    () => consoleApi.cities.list().then((r) => (Array.isArray(r) ? r : r?.cities || [])),
    [],
    { refetchOnWindowFocus: false }
  );
  // City names for the upload/edit/filter dropdowns (so tags match brief targeting).
  const cityOptions = useMemo(() => {
    const names = new Set();
    for (const c of Array.isArray(cities) ? cities : []) {
      const n = (c.name || c.city || "").trim();
      if (n) names.add(n);
    }
    for (const item of Array.isArray(uploaded) ? uploaded : []) {
      if (item.city) names.add(item.city);
    }
    return [...names].sort();
  }, [cities, uploaded]);

  const loading = loadingUploaded || loadingStock;

  const merged = useMemo(() => {
    const u = (Array.isArray(uploaded) ? uploaded : []).map(normalizeUploaded);
    const s = flattenStock(stock);
    // Uploaded first (most recently added is more relevant), then stock.
    return [...u, ...s];
  }, [uploaded, stock]);

  const allCategories = useMemo(() => {
    const seen = new Set(CATEGORY_OPTIONS.map((c) => c.value));
    const extras = [];
    for (const item of merged) {
      if (item.category && !seen.has(item.category)) {
        seen.add(item.category);
        extras.push({ value: item.category, label: item.category.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) });
      }
    }
    return [...CATEGORY_OPTIONS, ...extras];
  }, [merged]);

  const list = useMemo(() => {
    return merged.filter(
      (item) =>
        (!filterCategory || item.category === filterCategory) &&
        (!filterCity || (item.city || "") === filterCity)
    );
  }, [merged, filterCategory, filterCity]);

  const uploadedCount = (Array.isArray(uploaded) ? uploaded : []).length;
  const stockCount = flattenStock(stock).length;

  const handleScrapeClubs = async () => {
    const n = Number(botCount) || 3;
    setBusy(`Scraping ${n} club${n === 1 ? "" : "s"} in ${botCity}…`);
    setError(null);
    setBotResult(null);
    try {
      const res = await consoleApi.content.runScreenshotBot({ city: botCity, count: n });
      setBotResult(res);
      await refetch();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Scrape failed");
    } finally {
      setBusy(null);
    }
  };

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(`Uploading ${files.length} file${files.length === 1 ? "" : "s"}…`);
    setError(null);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", uploadCategory);
        fd.append("label", file.name);
        if (uploadCity) fd.append("city", uploadCity);
        await consoleApi.content.uploadScreenshot(fd);
      }
      await refetch();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Upload failed");
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this screenshot?")) return;
    setBusy("Deleting…");
    setError(null);
    try {
      await consoleApi.content.deleteScreenshot(id);
      await refetch();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Delete failed");
    } finally {
      setBusy(null);
    }
  };

  const startRename = (item) => {
    setEditingId(item.id);
    setEditLabel(item.label || "");
    setEditCity(item.city || "");
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditLabel("");
    setEditCity("");
  };

  const saveRename = async (id) => {
    const label = editLabel.trim();
    if (!label) {
      cancelRename();
      return;
    }
    setBusy("Saving…");
    setError(null);
    try {
      await consoleApi.content.updateScreenshot(id, { label, city: editCity });
      cancelRename();
      await refetch();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setBusy(null);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files || []).filter((f) =>
      (f.type || "").startsWith("image/")
    );
    if (files.length > 0) handleFiles(files);
  };

  return (
    <div className="ch-shots">
      {/* Upload zone */}
      <div
        className={`ch-shots__drop ${dragOver ? "is-dragover" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        <div className="ch-shots__drop-text">
          <div className="ch-shots__drop-title">Drop screenshots here</div>
          <div className="ch-shots__drop-sub">PNG, JPG, WebP, HEIC — they persist across deploys</div>
        </div>
        <div className="ch-shots__drop-actions">
          <label className="ch-shots__drop-cat">
            <span>Category</span>
            <select
              value={uploadCategory}
              onChange={(e) => setUploadCategory(e.target.value)}
            >
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </label>
          <label className="ch-shots__drop-cat">
            <span>City</span>
            <select value={uploadCity} onChange={(e) => setUploadCity(e.target.value)}>
              <option value="">Generic (any city)</option>
              {cityOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="content-hub__btn content-hub__btn--primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={!!busy}
          >
            Choose files
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              handleFiles(Array.from(e.target.files || []));
              e.target.value = "";
            }}
          />
        </div>
      </div>

      {/* Club screenshot bot — capture native app club screens for a city */}
      <div className="ch-shots__drop" style={{ cursor: "default" }}>
        <div className="ch-shots__drop-text">
          <div className="ch-shots__drop-title">Scrape club screens</div>
          <div className="ch-shots__drop-sub">
            Capture native app club cards for a city, skipping clubs already done.
            Runs where the backend runs — needs the iOS Simulator (local).
          </div>
        </div>
        <div className="ch-shots__drop-actions">
          <label className="ch-shots__drop-cat">
            <span>City</span>
            <select value={botCity} onChange={(e) => setBotCity(e.target.value)}>
              {!cityOptions.includes(botCity) && botCity && (
                <option value={botCity}>{botCity}</option>
              )}
              {cityOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>
          <label className="ch-shots__drop-cat">
            <span>Clubs</span>
            <input
              type="number"
              min="1"
              max="25"
              value={botCount}
              onChange={(e) => setBotCount(e.target.value)}
              style={{ width: 60 }}
            />
          </label>
          <button
            type="button"
            className="content-hub__btn content-hub__btn--primary"
            onClick={handleScrapeClubs}
            disabled={!!busy}
          >
            Scrape clubs
          </button>
        </div>
      </div>

      {botResult && (
        <div className="content-hub__hint">
          Captured {botResult.captured?.length || 0} screen
          {(botResult.captured?.length || 0) === 1 ? "" : "s"} from {botResult.selected || 0} club
          {(botResult.selected || 0) === 1 ? "" : "s"} in {botResult.city}
          {botResult.errors?.length ? ` · ${botResult.errors.length} error(s)` : ""}
          {botResult.note ? ` — ${botResult.note}` : ""}.
        </div>
      )}

      {busy && <div className="content-hub__busy">{busy}</div>}
      {error && <div className="content-hub__error">{error}</div>}

      {/* Toolbar */}
      <div className="ch__toolbar">
        <div className="ch__toolbar-left">
          <div className="ch__toolbar-title">
            <span className="ch__toolbar-eyebrow">Screenshots</span>
            <span className="ch__toolbar-count">
              {list.length} {list.length === 1 ? "image" : "images"}
              {(uploadedCount > 0 || stockCount > 0) && (
                <span className="ch-shots__source-split">
                  {" · "}{uploadedCount} uploaded · {stockCount} stock
                </span>
              )}
            </span>
          </div>
        </div>
        <div className="ch__filters">
          <select
            className="ch__select"
            value={filterCity}
            onChange={(e) => setFilterCity(e.target.value)}
          >
            <option value="">All cities</option>
            {cityOptions.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select
            className="ch__select"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="">All categories</option>
            {allCategories.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          {(filterCategory || filterCity) && (
            <button
              type="button"
              className="ch__btn ch__btn--link"
              onClick={() => {
                setFilterCategory("");
                setFilterCity("");
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {loading && <p className="content-hub__hint">Loading screenshots…</p>}
      {!loading && list.length === 0 && (
        <div className="content-hub__empty">
          <p>
            {filterCategory
              ? `No screenshots in "${filterCategory}". Try clearing the filter.`
              : "No screenshots yet. Drop some above to start building your library."}
          </p>
        </div>
      )}

      <div className="ch-lib__grid">
        {list.map((item) => (
          <div key={item.id} className={`ch-lib__card ch-shots__card ch-shots__card--${item.source}`}>
            <button
              type="button"
              className="ch-shots__card-thumb-btn"
              onClick={() => setActive(item)}
              title={item.label}
            >
              <div className="ch-lib__thumb">
                <img src={item.url} alt={item.label} loading="lazy" />
                <span
                  className={`ch-shots__source-badge ch-shots__source-badge--${item.source}`}
                  title={item.source === "stock" ? "Built-in from marketing/stock/" : "Uploaded via UI"}
                >
                  {item.source === "stock" ? "Stock" : "Upload"}
                </span>
              </div>
            </button>
            <div className="ch-lib__card-meta">
              {editingId === item.id ? (
                <div className="ch-shots__rename">
                  <input
                    type="text"
                    value={editLabel}
                    autoFocus
                    maxLength={200}
                    onChange={(e) => setEditLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveRename(item.id);
                      if (e.key === "Escape") cancelRename();
                    }}
                    placeholder="Descriptive title (the AI picks by this)"
                  />
                  <select
                    className="ch__select"
                    value={editCity}
                    onChange={(e) => setEditCity(e.target.value)}
                    title="City this screenshot is for (scopes which briefs can use it)"
                  >
                    <option value="">Generic (any city)</option>
                    {cityOptions.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <div className="ch-shots__rename-actions">
                    <button type="button" className="ch-shots__copy-btn" onClick={() => saveRename(item.id)}>Save</button>
                    <button type="button" className="ch-shots__delete-btn" onClick={cancelRename}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="ch-lib__card-topic">{item.label}</div>
              )}
              <div className="ch-lib__card-tags">
                {item.city && <span className="content-hub__tag content-hub__tag--posted">{item.city}</span>}
                <span className="content-hub__tag">{item.category}</span>
                {item.width && item.height && (
                  <span className="content-hub__tag content-hub__tag--soft">
                    {item.width}×{item.height}
                  </span>
                )}
              </div>
              {editingId !== item.id && (
                <div className="ch-shots__card-actions">
                  <button
                    type="button"
                    className="ch-shots__copy-btn"
                    onClick={() => {
                      navigator.clipboard?.writeText(item.url);
                    }}
                    title="Copy image URL"
                  >
                    Copy URL
                  </button>
                  {item.deletable && (
                    <button
                      type="button"
                      className="ch-shots__copy-btn"
                      onClick={() => startRename(item)}
                      title="Rename — give it a clear title so the AI picks it correctly"
                    >
                      Rename
                    </button>
                  )}
                  {item.deletable && (
                    <button
                      type="button"
                      className="ch-shots__delete-btn"
                      onClick={() => handleDelete(item.id)}
                      title="Delete"
                    >
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <Lightbox item={active} onClose={() => setActive(null)} />
    </div>
  );
}
